import React, { useState } from 'react';
import { api } from '../api';
import { toast } from 'sonner';
import { Mail, Loader2, ArrowRight, CheckCircle2 } from 'lucide-react';

// ----------------------------------------------------------------------
// BlogSubscribe
// ----------------------------------------------------------------------
// Public "stay in touch" opt-in shown at the bottom of /blog and every
// article page. Submits to POST /api/blog/subscribe which is idempotent
// (existing address = quiet 200), and links every capture into the admin
// subscribers view at /admin/blog (Subscribers tab).
// ----------------------------------------------------------------------

export const BlogSubscribe = ({ source = 'blog', compact = false }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | ok | error

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    try {
      await api.post('/blog/subscribe', { email: email.trim(), source });
      setStatus('ok');
      toast.success('You\u2019re on the list \u2014 talk soon.');
      setEmail('');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Try again.';
      toast.error(msg);
      setStatus('error');
    }
  };

  const wrapper = compact
    ? 'rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5'
    : 'rounded-2xl border border-slate-200 dark:border-slate-800 bg-gradient-to-r from-[#1B3A87]/5 via-white to-[#C8102E]/5 dark:from-[#1B3A87]/20 dark:via-slate-900 dark:to-[#C8102E]/20 p-6 md:p-8';

  // Scope the heading id + testid by `source` so multiple BlogSubscribe
  // instances (blog page + landing page marquee, etc.) can coexist on the
  // same document without creating duplicate ids or invalidating tests.
  const headingId = `blog-subscribe-heading-${source}`;

  return (
    <section
      className={wrapper}
      aria-labelledby={headingId}
      data-testid={`blog-subscribe-${source}`}
    >
      <div className='flex items-start gap-3'>
        <div className='hidden sm:flex w-10 h-10 rounded-lg bg-[#1B3A87]/10 dark:bg-[#93C5FD]/15 text-[#1B3A87] dark:text-[#93C5FD] items-center justify-center'>
          <Mail className='w-5 h-5' />
        </div>
        <div className='flex-1 min-w-0'>
          <div className='text-[11px] uppercase tracking-[0.18em] font-bold text-[#C8102E]'>Stay in touch</div>
          <h3
            id={headingId}
            className={`font-display font-bold text-[#0B1220] dark:text-slate-50 leading-tight mt-1 ${compact ? 'text-base' : 'text-lg md:text-xl'}`}
          >
            One dog-trainer SEO article a week &mdash; straight to your inbox.
          </h3>
          <p className='mt-1 text-sm text-slate-600 dark:text-slate-300'>
            New posts every Sunday morning. Unsubscribe any time.
          </p>

          <form onSubmit={submit} className='mt-4 flex flex-col sm:flex-row gap-2' data-testid='blog-subscribe-form'>
            <label htmlFor={`blog-subscribe-email-${source}`} className='sr-only'>Email address</label>
            <input
              id={`blog-subscribe-email-${source}`}
              type='email'
              required
              autoComplete='email'
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (status === 'error') setStatus('idle'); }}
              placeholder='you@your-training-business.com'
              className='flex-1 px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm text-[#0B1220] dark:text-slate-50 placeholder:text-slate-400 focus:outline-none focus:border-[#1B3A87] dark:focus:border-[#93C5FD] focus:ring-2 focus:ring-[#1B3A87]/20'
              data-testid='blog-subscribe-email'
            />
            <button
              type='submit'
              disabled={status === 'loading'}
              className='shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#C8102E] hover:bg-[#A50D26] disabled:opacity-60 text-white text-sm font-bold shadow-md transition-colors'
              data-testid='blog-subscribe-submit'
            >
              {status === 'loading' ? (
                <><Loader2 className='w-4 h-4 animate-spin' /> Subscribing&hellip;</>
              ) : status === 'ok' ? (
                <><CheckCircle2 className='w-4 h-4' /> Subscribed</>
              ) : (
                <>Subscribe <ArrowRight className='w-4 h-4' /></>
              )}
            </button>
          </form>
          {status === 'ok' && (
            <p className='mt-3 text-xs text-emerald-700 dark:text-emerald-300 font-semibold' data-testid='blog-subscribe-success'>
              You&rsquo;re on the list. Look for the first Sunday drop.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default BlogSubscribe;
